import { ProjectType } from "../../../interfaces/project"
import { formatMoneyDecimals, formatDateToDMY } from "../../../utils/Functions"
import { isEstimatorDecimalProject } from "../../../utils/estimatorConstants"
import React, { useState, useEffect } from 'react';

const today = formatDateToDMY(new Date().toISOString(),'/')


export const Payment = ({ 
    project, 
    pie, 
    currency, 
    exchange_rate,
    total,
    originalPrice = 0, 
    discount = 0, 
    discountPercentage = 0,
    allData
}: {
    project: ProjectType,
    pie: number, 
    currency: string,
    exchange_rate?: number,
    total: number,
    originalPrice: number,
    discount?: number,
    discountPercentage?: number,
    allData: any
}) => {
    const [divisor, setDivisor] = useState(1);

    useEffect(() => {
        // Fetch UF value from API
        fetch('https://mindicador.cl/api/uf')
            .then(response => response.json())
            .then(data => {
                const latestUfValue = data.serie[0].valor;
                setDivisor(latestUfValue);
                console.log('Valor de la UF:', latestUfValue); // Log the UF value
            })
            .catch(error => console.error('Error fetching UF value:', error));
    }, []);

    // Determinar si mostramos el precio original y descuento
    const showDiscount = discount > 0 && originalPrice > 0;
    
    // Verificar si hay préstamo aprobado
    const hasApprovedLoan = allData.bank_preapproval !== '0';
    
    // Verificar si es bono pie o descuento
    const isBonoPie = allData.discount_type === 'Bono Pie';
    const isDiscount = allData.discount_type === 'Descuento';
    
    // Detectar si el bono pie debe sumarse (proyectos especiales)
    const projectId = Number(project.id || project.identifier);
    
    // Calcular descuento con decimales si es proyecto especial
    let calculatedDiscount = discount;
    if (isDiscount && isEstimatorDecimalProject(projectId)) {
        // Para proyectos especiales, calcular descuento con decimales
        const discountValueStr = allData.discount_value.replace(',', '.');
        const discountPercentage = parseFloat(discountValueStr);
        calculatedDiscount = (discountPercentage / 100) * originalPrice;
    }
    
    // Usar el valor de reserva del proyecto
    let reservaValue = project.deposit;
    let reservaAmount = 0;

    // Verificar si el valor de reserva es un string
    if (typeof reservaValue === 'string') {
        if (reservaValue.includes('UF')) {
            // Si es en formato "10UF", extraer el número
            const numericValue = parseFloat(reservaValue.replace('UF', '').trim());
            reservaAmount = numericValue; // Usar el valor directamente como monto
        } else {
            // Si es un número en string, convertirlo a número
            const numericValue = parseFloat(reservaValue) / divisor;
            reservaAmount = numericValue; // Usar el valor directamente como monto
        }
    } else {
        // Si ya es un número, usarlo directamente
        reservaAmount = reservaValue;
    }
    
    // Detectar si el bono pie debe sumarse (proyectos especiales)
    // COMENTADO: El bono pie NO debe sumarse al precio, debe ser parte del 100% del total
    // const bonoPieSuma = isBonoPie && [2553, 2554, 2555, 2558, 2884, 2975, 3031, 3219, 3220, 3253, 3260, 3338, 3378, 3398, 3402, 3407, 3408, 3409].includes(projectId);
    const bonoPieSuma = false; // Forzado a false: tratar todos los proyectos igual (bono pie como parte del 100%, no sumado al precio)

    // Calcular precios individuales
    let unitPriceAfterDiscount = showDiscount ? originalPrice - calculatedDiscount : originalPrice;
    let bonoPieExtra = 0;
    if (bonoPieSuma) {
        // Si el bono pie suma, el precio final es el original + bono pie
        // Convertir valor con coma a punto para procesamiento si es proyecto especial
        const bonusValueStr = allData.discount_value.replace(',', '.');
        const bonusPercentage = parseFloat(bonusValueStr);
        bonoPieExtra = (bonusPercentage / 100) * originalPrice;
        unitPriceAfterDiscount = originalPrice + bonoPieExtra;
    }
    const garagePrice = parseFloat(allData.garage_price) || 0;
    const warehousePrice = parseFloat(allData.warehouse_price) || 0;

    // El total final debe incluir: unidad (con bono pie sumado si aplica) + garage + warehouse
    const totalAfterDiscount = unitPriceAfterDiscount + garagePrice + warehousePrice;
    
    // Calcular el porcentaje restante para pie, bono pie y financiamiento
    const financingPercentage = hasApprovedLoan ? parseFloat(allData.bank_preapproval) || 0 : 0;
    let bonoPiePercentage = isBonoPie ? parseFloat(allData.discount_value.replace(',', '.')) || 0 : 0;
    let piePercentage = 100 - financingPercentage;
    // Para proyectos especiales, el bono pie no afecta el pie
    if (!bonoPieSuma && isBonoPie) {
        piePercentage -= bonoPiePercentage;
    }

    // Ajustar el bono pie para que no exceda el 100% con los otros componentes
    if (hasApprovedLoan && isBonoPie && !bonoPieSuma) {
        const maxBonoPie = 100 - financingPercentage;
        if (bonoPiePercentage > maxBonoPie) {
            bonoPiePercentage = maxBonoPie;
        }
        piePercentage = 100 - financingPercentage - bonoPiePercentage;
    }

    // Monto financiado por el banco (solo si hay préstamo aprobado)
    let financingAmount = 0;
    let pieAmount = 0;
    if (hasApprovedLoan) {
        if (bonoPieSuma) {
            // Para proyectos especiales, el pie y el financiamiento se calculan sobre el precio base
            financingAmount = (financingPercentage / 100) * originalPrice;
            pieAmount = originalPrice - financingAmount;
        } else {
            financingAmount = (financingPercentage / 100) * totalAfterDiscount;
            pieAmount = (piePercentage / 100) * totalAfterDiscount;
        }
    }

    // Calcular el monto del bono pie (solo si es bono pie)
    let bonoPieAmount = 0;
    let precioFinalEspecial = 0;
    if (isBonoPie) {
        if (bonoPieSuma) {
            // Para IDs especiales: calcular precio final = precio unidad ÷ (1 - bonoPie/100)
            const bonoPiePercentage = parseFloat(allData.discount_value.replace(',', '.')) || 0;
            const divisor = 1 - (bonoPiePercentage / 100);
            precioFinalEspecial = originalPrice / divisor;
            bonoPieAmount = precioFinalEspecial - originalPrice;
        } else {
            bonoPieAmount = (bonoPiePercentage / 100) * totalAfterDiscount;
        }
    }
    // Si es especial, el precio final y el total deben ser el calculado
    const precioUnidadFinal = bonoPieSuma && isBonoPie ? precioFinalEspecial : unitPriceAfterDiscount;
    const totalAfterDiscountFinal = bonoPieSuma && isBonoPie ? precioFinalEspecial : totalAfterDiscount;
    
    // Verificar si hay estacionamiento o bodega
    const hasGarage = allData.garage && parseFloat(allData.garage_price) > 0;
    const hasWarehouse = allData.warehouse && parseFloat(allData.warehouse_price) > 0;
    
    // Calcular cuánto resta pagar después de la reserva
    let pieTotal = bonoPieSuma && isBonoPie ? pieAmount + bonoPieAmount : pieAmount;
    let pieTotalConReserva = pieTotal - reservaAmount;
    let financiamientoConReserva = financingAmount;
    // Si la reserva es mayor al pie, el resto se descuenta del financiamiento
    if (pieTotalConReserva < 0) {
        financiamientoConReserva = financiamientoConReserva + pieTotalConReserva; // pieTotalConReserva es negativo
        pieTotalConReserva = 0;
    }
    
    return (
        <>
            <p className="fw-bold mb-1">III. FORMAS DE PAGO</p>
            <div className="px-2">
                {/* Sección de precio y descuento */}
                <div className="mb-2 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '0.8rem' }}>
                    <h6 className="fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Detalle del precio</h6>
                    
                    {/* Precio de la unidad con posible descuento */}
                    <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                        <span>Precio Unidad (Original):</span>
                        <span>{currency} {formatMoneyDecimals(originalPrice)}</span>
                    </div>
                    
                    {/* Mostrar bono pie como línea adicional si suma */}
                    {bonoPieSuma && (
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                            <span>Bono Pie (adicional{bonoPiePercentage ? `, ${bonoPiePercentage}%` : ''}):</span>
                            <span>{currency} {formatMoneyDecimals(bonoPieAmount)}</span>
                        </div>
                    )}
                    
                    {/* Mostrar descuento si aplica */}
                    {showDiscount && !bonoPieSuma && (
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                            <span>Descuento ({discountPercentage}%):</span>
                            <span>- {currency} {formatMoneyDecimals(calculatedDiscount)}</span>
                        </div>
                    )}
                    
                    {/* Precio después del descuento o suma */}
                    <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                        <span>Precio Unidad (Final):</span>
                        <span>{currency} {formatMoneyDecimals(precioUnidadFinal)}</span>
                    </div>
                    
                    {/* Estacionamiento si aplica */}
                    {hasGarage && (
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                            <span>Estacionamiento {allData.garage?.name}:</span>
                            <span>{currency} {formatMoneyDecimals(garagePrice)}</span>
                        </div>
                    )}
                    
                    {/* Bodega si aplica */}
                    {hasWarehouse && (
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                            <span>Bodega {allData.warehouse?.name}:</span>
                            <span>{currency} {formatMoneyDecimals(warehousePrice)}</span>
                        </div>
                    )}
                    
                    {/* Línea divisoria */}
                    <hr style={{ margin: '0.3rem 0' }} />
                    
                    {/* Precio total */}
                    <div className="d-flex justify-content-between">
                        <span className="fw-bold" style={{ fontSize: '0.85rem' }}>PRECIO TOTAL:</span>
                        <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{currency} {formatMoneyDecimals(totalAfterDiscountFinal)}</span>
                    </div>
                </div>

                {/* Tabla de desglose de pagos - Versión más compacta */}
                <div className="mb-2 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '0.8rem' }}>
                    <h6 className="fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Desglose de pagos</h6>
                    
                    <div className="d-flex justify-content-between mb-1 pb-1" style={{ borderBottom: '1px solid #dee2e6', fontSize: '0.8rem' }}>
                        <div style={{ width: '50%' }}>Concepto</div>
                        <div style={{ width: '25%', textAlign: 'center' }}>%</div>
                        <div style={{ width: '25%', textAlign: 'right' }}>Monto</div>
                    </div>
                    
                    {/* Reserva (siempre se muestra) */}
                    <div className="d-flex justify-content-between mb-1 py-1" style={{ fontSize: '0.8rem' }}>
                        <div style={{ width: '50%' }}>
                            <span className="fw-bold">Reserva</span>
                            <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>Pago inicial (incluido en el total)</small>
                        </div>
                        <div style={{ width: '25%', textAlign: 'center' }}>-</div>
                        <div style={{ width: '25%', textAlign: 'right' }}>{currency} {formatMoneyDecimals(reservaAmount)}</div>
                    </div>
                    
                    {/* Pie (solo si hay préstamo aprobado y el porcentaje es mayor a 0) */}
                    {hasApprovedLoan && pieAmount > 0 && !(bonoPieSuma && isBonoPie) && (
                        <div className="d-flex justify-content-between mb-1 py-1" style={{ fontSize: '0.8rem' }}>
                            <div style={{ width: '50%' }}>
                                <span className="fw-bold">Pie</span>
                                <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>Pago adicional</small>
                            </div>
                            <div style={{ width: '25%', textAlign: 'center' }}>{formatMoneyDecimals(piePercentage) + '%'}</div>
                            <div style={{ width: '25%', textAlign: 'right' }}>{currency} {formatMoneyDecimals(pieTotalConReserva)}</div>
                        </div>
                    )}
                    
                    {/* Bono Pie (solo si es bono pie y NO es proyecto especial) */}
                    {isBonoPie && !bonoPieSuma && (
                        <div className="d-flex justify-content-between mb-1 py-1" style={{ fontSize: '0.8rem' }}>
                            <div style={{ width: '50%' }}>
                                <span className="fw-bold">Bono Pie</span>
                                <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>Beneficio promocional</small>
                            </div>
                            <div style={{ width: '25%', textAlign: 'center' }}>{formatMoneyDecimals(bonoPiePercentage) + '%'}</div>
                            <div style={{ width: '25%', textAlign: 'right' }}>{currency} {formatMoneyDecimals(bonoPieAmount)}</div>
                        </div>
                    )}
                    
                    {/* Pie Total (solo para IDs especiales con bono pie, antes de financiamiento) */}
                    {bonoPieSuma && isBonoPie && hasApprovedLoan && pieAmount > 0 && (
                        <div className="d-flex justify-content-between mb-1 py-1" style={{ fontSize: '0.8rem' }}>
                            <div style={{ width: '50%' }}>
                                <span className="fw-bold">Pie Total{bonoPiePercentage ? ` (${bonoPiePercentage}%)` : ''}</span>
                                <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>Pie + Bono Pie</small>
                            </div>
                            <div style={{ width: '25%', textAlign: 'center' }}>-</div>
                            <div style={{ width: '25%', textAlign: 'right' }}>{currency} {formatMoneyDecimals(pieTotalConReserva)}</div>
                        </div>
                    )}
                    
                    {/* Financiamiento bancario (solo si hay préstamo aprobado) */}
                    {hasApprovedLoan && (
                        <div className="d-flex justify-content-between mb-1 py-1" style={{ fontSize: '0.8rem' }}>
                            <div style={{ width: '50%' }}>
                                <span className="fw-bold">Financiamiento</span>
                                <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>Crédito hipotecario</small>
                            </div>
                            <div style={{ width: '25%', textAlign: 'center' }}>{formatMoneyDecimals(financingPercentage)}%</div>
                            <div style={{ width: '25%', textAlign: 'right' }}>{currency} {formatMoneyDecimals(financiamientoConReserva)}</div>
                        </div>
                    )}
                    
                    {/* Total */}
                    <div className="d-flex justify-content-between pt-1 mt-1" style={{ borderTop: '1px solid #dee2e6', fontSize: '0.8rem' }}>
                        <div style={{ width: '50%' }}>
                            <span className="fw-bold">TOTAL</span>
                        </div>
                        <div style={{ width: '25%', textAlign: 'center' }}>100%</div>
                        <div style={{ width: '25%', textAlign: 'right', fontWeight: 'bold' }}>{currency} {formatMoneyDecimals(totalAfterDiscountFinal)}</div>
                    </div>
                </div>
                
                {/* Nota sobre tipo de cambio - Más compacta */}
                {!!exchange_rate && 
                    <p className="mt-1 text-muted" style={{ fontSize: '0.7rem', fontStyle: 'italic', marginBottom: '0.3rem' }}>
                        * Los montos en UF corresponden al valor de la UF de $ {formatMoneyDecimals(exchange_rate)} al {today}, por lo cual solo se citan de referencia.
                    </p>
                }

                {/* Aviso legal - Más compacto */}
                <div className="border rounded-2 px-2 py-1 mt-1" style={{ backgroundColor: '#f8f9fa' }}>
                    <p style={{ fontStyle: 'italic', textAlign: 'center', margin: 0, fontSize: '0.75rem' }}>
                        <strong>La presente cotización no es considerada como una reserva, por lo que no asegura la disponibilidad de las unidades cotizadas al momento de proceder a la compra.</strong>
                    </p>
                </div>
            </div>
        </>
    )
}   